import { WORKFLOW_CANVAS as C } from "@sagot/constants";
import { useEffect, useRef, useState, type PointerEvent, type RefObject } from "react";
import type { ExitRef } from "../exit-slots";
import type { Box, Point } from "./canvas-layout";
import { autoPanDelta, clientToCanvas, isDrag, tileAt } from "./connector-geometry";
import { prefersReducedMotion } from "./motion";

/** Where a drag began: the handle on an exit, or the grip at the target end of its selected connector. */
export type DragOrigin = "handle" | "grip";

/** A connector being dragged: the exit it comes from, where the pointer is on the canvas, and the tile under it. */
export interface DragSession {
  source: ExitRef;
  origin: DragOrigin;
  pointer: Point;
  over: string | null;
}

/** Pointer handlers for an element that starts a drag (a handle, or the grip of a selected connector). */
export interface PointerBindings {
  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
  onPointerCancel: (event: PointerEvent) => void;
  onLostPointerCapture: (event: PointerEvent) => void;
}

interface Press {
  source: ExitRef;
  origin: DragOrigin;
  pointerId: number;
  start: Point;
  moved: boolean;
}

interface Options {
  viewport: RefObject<HTMLDivElement | null>;
  zoom: number;
  boxes: Readonly<Record<string, Box>>;
  editable: boolean;
  /** The pointer travelled far enough: this is a drag, not a click. */
  onStart: (source: ExitRef) => void;
  /** The pointer was released over the canvas: a tile id, or `null` over empty canvas at `point`. */
  onDrop: (source: ExitRef, over: string | null, point: Point) => void;
  onCancel: () => void;
}

export interface ConnectorDrag {
  session: DragSession | null;
  bind: (source: ExitRef, origin: DragOrigin) => PointerBindings;
  /** After a drag the browser still sends a click to the handle; this says whether to ignore it (once). */
  takeSwallowedClick: () => boolean;
}

const clientOf = (event: PointerEvent): Point => ({ x: event.clientX, y: event.clientY });

/**
 * Dragging a connector from an exit: the pointer is captured by the element it went down on, so the drag keeps working
 * outside the handle; tiles are found with geometry, not with the DOM, so it works at any zoom. Esc cancels, a drag that
 * ends outside the canvas is cancelled, and near the canvas edge it scrolls (in discrete steps with reduced motion).
 */
export function useConnectorDrag({ viewport, zoom, boxes, editable, onStart, onDrop, onCancel }: Options): ConnectorDrag {
  const [session, setSession] = useState<DragSession | null>(null);
  const press = useRef<Press | null>(null);
  const latest = useRef<Point>({ x: 0, y: 0 });
  const swallow = useRef(false);

  /** A point on screen as a point of the canvas, at the current scroll and zoom. */
  const locate = (client: Point): Point | null => {
    const element = viewport.current;
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    const origin = { x: rect.left + element.clientLeft, y: rect.top + element.clientTop };
    return clientToCanvas(client, origin, { x: element.scrollLeft, y: element.scrollTop }, zoom);
  };
  const follow = (client: Point) => {
    const pointer = locate(client);
    if (!pointer) return;
    const over = tileAt(pointer, boxes);
    setSession((current) => (current ? { ...current, pointer, over } : current));
  };
  // The auto-pan timer outlives a render: it calls whichever `follow` is current.
  const followRef = useRef(follow);
  useEffect(() => { followRef.current = follow; });

  const stop = (event?: PointerEvent) => {
    press.current = null;
    if (event?.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    setSession(null);
  };
  const cancel = (event?: PointerEvent) => {
    const wasDragging = press.current?.moved === true;
    stop(event);
    if (wasDragging) onCancel();
  };
  const cancelRef = useRef(cancel);
  useEffect(() => { cancelRef.current = cancel; });

  const finish = (event: PointerEvent, drag: Press) => {
    const client = clientOf(event);
    const element = viewport.current;
    const rect = element?.getBoundingClientRect();
    const inside = rect !== undefined && client.x >= rect.left && client.x <= rect.right && client.y >= rect.top && client.y <= rect.bottom;
    const point = locate(client);
    if (!inside || !point) {
      onCancel();
      return;
    }
    onDrop(drag.source, tileAt(point, boxes), point);
  };

  const bind = (source: ExitRef, origin: DragOrigin): PointerBindings => ({
    onPointerDown: (event) => {
      if (!editable || event.button !== 0) return;
      event.currentTarget.setPointerCapture(event.pointerId);
      swallow.current = false;
      latest.current = clientOf(event);
      press.current = { source, origin, pointerId: event.pointerId, start: clientOf(event), moved: false };
    },
    onPointerMove: (event) => {
      const drag = press.current;
      if (drag?.pointerId !== event.pointerId) return;
      latest.current = clientOf(event);
      if (!drag.moved) {
        if (!isDrag(drag.start, latest.current)) return;
        drag.moved = true;
        swallow.current = true;
        setSession({ source, origin, pointer: locate(latest.current) ?? { x: 0, y: 0 }, over: null });
        onStart(source);
      }
      follow(latest.current);
    },
    onPointerUp: (event) => {
      const drag = press.current;
      if (drag?.pointerId !== event.pointerId) return;
      stop(event);
      if (drag.moved) finish(event, drag);
    },
    onPointerCancel: (event) => { if (press.current?.pointerId === event.pointerId) cancel(event); },
    onLostPointerCapture: (event) => { if (press.current?.pointerId === event.pointerId) cancel(); },
  });

  const dragging = session !== null;
  useEffect(() => {
    if (!dragging) return undefined;
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") cancelRef.current(); };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); };
  }, [dragging]);

  useEffect(() => {
    const element = viewport.current;
    if (!dragging || !element) return undefined;
    const pan = (discrete: boolean) => {
      const delta = autoPanDelta(latest.current, element.getBoundingClientRect());
      if (delta.x === 0 && delta.y === 0) return;
      const amount = (value: number) => (discrete ? Math.sign(value) * C.autoPanReducedStep : value);
      element.scrollBy({ left: amount(delta.x), top: amount(delta.y) });
      followRef.current(latest.current);
    };
    if (prefersReducedMotion()) {
      const timer = window.setInterval(() => { pan(true); }, C.autoPanReducedMs);
      return () => { window.clearInterval(timer); };
    }
    let frame = 0;
    const tick = () => {
      pan(false);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => { window.cancelAnimationFrame(frame); };
  }, [dragging, viewport]);

  return {
    session,
    bind,
    takeSwallowedClick: () => {
      const swallowed = swallow.current;
      swallow.current = false;
      return swallowed;
    },
  };
}
